import { useReducer } from "react";
import { useMutation } from "@tanstack/react-query";

import { track } from "@/lib/analytics/data-layer";
import { completeOnboardingStep } from "@/lib/analytics/onboarding-funnel";
import { clientPost } from "@/lib/http/client-http";
import { toBackendAddress } from "@/lib/mappers/address";
import { toBackendBirthDate, toBackendGender } from "@/lib/mappers/customer-profile";
import { leaveForDocument } from "@/lib/motion/page-transition";
import { CODE_ERRORS, REGISTER_ERRORS, REGISTER_FALLBACK_ERROR, CONFIRM_REGISTER_FALLBACK_ERROR, CONFIRM_REGISTER_LOGIN_FAILED, LOGIN_FALLBACK_ERROR, ADDRESS_SAVE_ERROR, REGISTER_TRANSPORT_ERROR, RESEND_CODE_ERROR, CONFIRM_REGISTER_TRANSPORT_ERROR, LOGIN_TRANSPORT_ERROR } from "@/lib/errors/error-messages";
import type { EightDigitCodeValues, SixDigitCodeValues } from "@/lib/auth/code-schemas";
import { listCreationDestination, type OnboardingIntent } from "@/lib/onboarding/onboarding-intent";
import type { AddressStepValues, OnboardingSignupValues } from "@/lib/onboarding/onboarding-schemas";

import type { SignupStep } from "@/lib/onboarding/onboarding-types";
export type { SignupStep } from "@/lib/onboarding/onboarding-types";

// ─── Types ───────────────────────────────────────────────────────────────────

type SignupState = {
  step: SignupStep;
  maskedEmail: string;
  signupData: OnboardingSignupValues | null;
  addressData: AddressStepValues | null;
  /**
   * The wizard is over and the next page is loading. Whatever step is on screen stays locked
   * until it arrives — a code that was accepted is not typed again. Only an error hands it back.
   */
  leaving: boolean;
  errors: {
    signup: string | null;
    signupEmail: string | null;
    address: string | null;
    confirmRegister: string | null;
    confirmLogin: string | null;
  };
};

type SignupAction =
  | { type: "GO_TO"; step: SignupStep }
  | { type: "SET_ERROR"; field: keyof SignupState["errors"]; error: string | null }
  | { type: "SET_MASKED_EMAIL"; email: string }
  | { type: "CLEAR_ERROR"; field: keyof SignupState["errors"] }
  | { type: "SET_SIGNUP_DATA"; data: OnboardingSignupValues }
  | { type: "SET_ADDRESS_DATA"; data: AddressStepValues }
  | { type: "LEAVE" };

// ─── API response types ──────────────────────────────────────────────────────

type RegisterResponse = { ok: true } | { ok: false; code: string };
/** `null` when nothing was provisioned: a guest signs up to send a gift, not to keep a list. */
type ProvisionedList = { id: string; needsTermsAcceptance: boolean } | null;
type ConfirmRegisterResponse =
  | { ok: true; authenticated: true; giftList: ProvisionedList }
  | { ok: true; authenticated: false; maskedEmail: string | null; loginStarted: boolean }
  | { ok: false; code: "INVALID_CODE" | "SERVER_ERROR" };
type ResendResponse = { ok: true } | { ok: false };
type ConfirmLoginResponse =
  | { ok: true; giftList: ProvisionedList }
  | { ok: false; code: "INVALID_CODE" | "EXPIRED" | "SERVER_ERROR" };


// ─── Reducer ─────────────────────────────────────────────────────────────────

const INITIAL_ERRORS: SignupState["errors"] = {
  signup: null,
  signupEmail: null,
  address: null,
  confirmRegister: null,
  confirmLogin: null,
};

function reducer(state: SignupState, action: SignupAction): SignupState {
  switch (action.type) {
    case "GO_TO":
      return { ...state, step: action.step };
    case "SET_ERROR":
      return {
        ...state,
        errors: { ...state.errors, [action.field]: action.error },
        leaving: action.error === null ? state.leaving : false,
      };
    case "CLEAR_ERROR":
      return { ...state, errors: { ...state.errors, [action.field]: null } };
    case "SET_MASKED_EMAIL":
      return { ...state, maskedEmail: action.email };
    case "SET_SIGNUP_DATA":
      return { ...state, signupData: action.data };
    case "SET_ADDRESS_DATA":
      return { ...state, addressData: action.data };
    case "LEAVE":
      return { ...state, leaving: true };
    default:
      return state;
  }
}

// ─── Hook ────────────────────────────────────────────────────────────────────

interface UseSignupWizardOptions {
  cpf: string;
  /** Which door this is (`ONBOARDING_ENTRY`): it decides whether a list is provisioned. */
  intent: OnboardingIntent;
  /** Where the person goes once the session exists — the checkout they left, or the panel. */
  redirectTo: string;
  initialStep?: SignupStep;
  /** Skip register call — user already REGISTERED, just needs profile fields + terms. */
  skipConfirmation?: boolean;
}

export interface UseSignupWizardResult {
  step: SignupStep;
  maskedEmail: string;
  errors: SignupState["errors"];
  signupData: OnboardingSignupValues | null;
  addressData: AddressStepValues | null;
  isPending: {
    register: boolean;
    saveAddress: boolean;
    confirmRegister: boolean;
    confirmLogin: boolean;
    resendRegister: boolean;
    resendLogin: boolean;
  };
  actions: {
    submitSignup: (values: OnboardingSignupValues) => void;
    submitAddress: (values: AddressStepValues) => void;
    confirmRegister: (values: SixDigitCodeValues) => void;
    confirmLogin: (values: EightDigitCodeValues) => void;
    resendRegister: () => void;
    resendLogin: () => void;
    clearEmailError: () => void;
    goBack: (to: SignupStep) => void;
  };
}

export function useSignupWizard({
  cpf,
  intent,
  redirectTo,
  initialStep = "signup",
  skipConfirmation = false,
}: UseSignupWizardOptions): UseSignupWizardResult {
  const [state, dispatch] = useReducer(reducer, {
    step: initialStep,
    maskedEmail: "",
    signupData: null,
    addressData: null,
    leaving: false,
    errors: INITIAL_ERRORS,
  });

  // ─── Register mutation ─────────────────────────────────────────────────

  const registerMutation = useMutation({
    mutationFn: (payload: { signup: OnboardingSignupValues; address: AddressStepValues }) =>
      clientPost<RegisterResponse>("/api/auth/customer/register", {
        email: payload.signup.email,
        taxPayerId: cpf,
        firstName: payload.signup.firstName,
        lastName: payload.signup.lastName,
        phone: payload.signup.phone,
        /*
         * The birth date and the gender were collected and thrown away until 2026-10-08: the
         * backend took neither. It takes both now (`feature/customer-gender-birthdate`), and the
         * gender goes through a mapping because the form has four options and the enum has three
         * — "prefiro não informar" travels as absence (`toBackendGender`).
         *
         * `marketingOptIn` is still dropped, and still on the record as item 4 of
         * the backend team's queue: there is no column for it, and the API refuses a key it
         * does not know, so sending it would fail the whole registration.
         */
        gender: toBackendGender(payload.signup.gender),
        birthDate: toBackendBirthDate(payload.signup.birthDate),
        addresses: [toBackendAddress(payload.address)],
      }),
    onSuccess: (data) => {
      if (!data.ok) {
        const message = REGISTER_ERRORS[data.code] ?? REGISTER_FALLBACK_ERROR;
        if (data.code === "EMAIL_ALREADY_EXISTS") {
          dispatch({ type: "SET_ERROR", field: "signupEmail", error: message });
          dispatch({ type: "GO_TO", step: "signup" });
          return;
        }
        if (data.code === "CPF_ALREADY_REGISTERED") {
          dispatch({ type: "SET_ERROR", field: "signup", error: message });
          dispatch({ type: "GO_TO", step: "signup" });
          return;
        }
        // The backend doesn't say which field failed, so this stays on the address
        // step — identity fields were already validated in the prior one.
        // TODO(backend): return `field` in the error body. Registered with the backend team.
        dispatch({ type: "SET_ERROR", field: "address", error: message });
        return;
      }
      dispatch({ type: "CLEAR_ERROR", field: "signup" });
      dispatch({ type: "CLEAR_ERROR", field: "signupEmail" });
      dispatch({ type: "CLEAR_ERROR", field: "address" });
      completeOnboardingStep("address");
      dispatch({ type: "GO_TO", step: "confirmRegister" });
    },
    onError: () => dispatch({ type: "SET_ERROR", field: "address", error: REGISTER_TRANSPORT_ERROR }),
  });

  // ─── Register confirmation ─────────────────────────────────────────────

  const resendRegisterMutation = useMutation({
    mutationFn: () => clientPost<ResendResponse>("/api/auth/customer/register/resend", { email: state.signupData?.email }),
    onSuccess: (data) => {
      if (!data.ok) {
        dispatch({ type: "SET_ERROR", field: "confirmRegister", error: RESEND_CODE_ERROR });
      }
    },
    onError: () => dispatch({ type: "SET_ERROR", field: "confirmRegister", error: RESEND_CODE_ERROR }),
  });

  const confirmRegisterMutation = useMutation({
    mutationFn: (values: SixDigitCodeValues) =>
      clientPost<ConfirmRegisterResponse>("/api/auth/customer/register/confirm", {
        email: state.signupData?.email,
        cpf,
        code: values.code,
        intent,
      }),
    onSuccess: (data) => {
      if (!data.ok) {
        dispatch({ type: "SET_ERROR", field: "confirmRegister", error: CODE_ERRORS[data.code] ?? CONFIRM_REGISTER_FALLBACK_ERROR });
        return;
      }
      // The e-mail code was right: the account exists, whether or not the session came with it.
      completeOnboardingStep("register_code");
      track({ event: "sign_up", method: "cpf_otp" });

      // Auto-login: session already created server-side. A host's draft gift list (Requirement 9)
      // is provisioned in the same request — terms belong to that list, so if it still needs
      // acceptance, that happens here before redirecting, not inside the list creation. A
      // guest has no list, so there are no terms and the checkout is one step closer.
      if (data.authenticated) {
        dispatch({ type: "CLEAR_ERROR", field: "confirmRegister" });
        settleSession(data.giftList);
        return;
      }
      // Fallback: backend couldn't auto-login — proceed to login OTP step.
      if (!data.loginStarted) {
        dispatch({ type: "SET_ERROR", field: "confirmRegister", error: CONFIRM_REGISTER_LOGIN_FAILED });
        return;
      }
      dispatch({ type: "CLEAR_ERROR", field: "confirmRegister" });
      if (data.maskedEmail) dispatch({ type: "SET_MASKED_EMAIL", email: data.maskedEmail });
      dispatch({ type: "GO_TO", step: "confirmLogin" });
    },
    onError: () => dispatch({ type: "SET_ERROR", field: "confirmRegister", error: CONFIRM_REGISTER_TRANSPORT_ERROR }),
  });

  // ─── Login confirmation ────────────────────────────────────────────────

  const resendLoginMutation = useMutation({
    mutationFn: () => clientPost<ResendResponse>("/api/auth/customer/login/resend"),
    onSuccess: (data) => {
      if (!data.ok) {
        dispatch({ type: "SET_ERROR", field: "confirmLogin", error: RESEND_CODE_ERROR });
      }
    },
    onError: () => dispatch({ type: "SET_ERROR", field: "confirmLogin", error: RESEND_CODE_ERROR }),
  });

  const confirmLoginMutation = useMutation({
    mutationFn: (values: EightDigitCodeValues) =>
      // `afterSignup`: this second code closes a signup, which the session records (`signupPath`).
      clientPost<ConfirmLoginResponse>("/api/auth/customer/login/confirm", { ...values, intent, afterSignup: true }),
    onSuccess: (data) => {
      if (!data.ok) {
        dispatch({ type: "SET_ERROR", field: "confirmLogin", error: CODE_ERRORS[data.code] ?? LOGIN_FALLBACK_ERROR });
        return;
      }
      dispatch({ type: "CLEAR_ERROR", field: "confirmLogin" });
      completeOnboardingStep("signup_login_code");
      settleSession(data.giftList);
    },
    onError: () => dispatch({ type: "SET_ERROR", field: "confirmLogin", error: LOGIN_TRANSPORT_ERROR }),
  });

  /**
   * The one place the wizard ends.
   *
   * A host whose list still needs its terms leaves for the **creation**, where the terms are now
   * asked — after the event, as §5.6 of both design packages orders it, so the person agrees to
   * the terms of the thing they have just described. The wizard used to hold them for one more
   * step and ask here; that put the acceptance in two places, and the other one did not know
   * where to send people afterwards (the defect reported on 2026-10-08).
   *
   * Everything else leaves for wherever this door leads.
   */
  function settleSession(giftList: ProvisionedList) {
    leave(giftList?.needsTermsAcceptance ? listCreationDestination(redirectTo) : redirectTo);
  }

  /** The wizard's way out: a full load, so the session cookie set by the last POST rides along. */
  function leave(href: string) {
    dispatch({ type: "LEAVE" });
    leaveForDocument(href);
  }

  /*
   * No terms step here any more, and no acceptance call: both live in the creation
   * (`CreateListTerms`), which is the only screen that asks. What used to be this wizard's last
   * step is the reason the acceptance existed twice.
   */

  // ─── Address save (for returning users who skipped register) ─────────

  const saveAddressMutation = useMutation({
    mutationFn: (values: AddressStepValues) =>
      clientPost<{ ok: boolean }>("/api/auth/onboarding/address", toBackendAddress(values)),
    onSuccess: async (data) => {
      if (!data.ok) {
        dispatch({ type: "SET_ERROR", field: "address", error: ADDRESS_SAVE_ERROR });
        return;
      }
      dispatch({ type: "CLEAR_ERROR", field: "address" });
      // Sync session flags with backend truth before redirecting.
      // Without this, the session still has hasAddress=false and the user
      // would be sent back to the address step on next visit (duplicating the address).
      await clientPost("/api/auth/onboarding/complete", {}).catch(() => {});
      completeOnboardingStep("address");
      leave(redirectTo);
    },
    onError: () => dispatch({ type: "SET_ERROR", field: "address", error: ADDRESS_SAVE_ERROR }),
  });

  // ─── Actions ───────────────────────────────────────────────────────────

  function submitSignup(values: OnboardingSignupValues) {
    dispatch({ type: "SET_SIGNUP_DATA", data: values });
    dispatch({ type: "CLEAR_ERROR", field: "signup" });
    dispatch({ type: "CLEAR_ERROR", field: "signupEmail" });
    completeOnboardingStep("signup");
    dispatch({ type: "GO_TO", step: "address" });
  }

  function submitAddress(values: AddressStepValues) {
    dispatch({ type: "SET_ADDRESS_DATA", data: values });

    if (skipConfirmation) {
      // User already REGISTERED — save address via dedicated endpoint.
      saveAddressMutation.mutate(values);
      return;
    }

    registerMutation.mutate({ signup: state.signupData!, address: values });
  }

  function goBack(to: SignupStep) {
    dispatch({ type: "GO_TO", step: to });
  }

  return {
    step: state.step,
    maskedEmail: state.maskedEmail,
    errors: state.errors,
    signupData: state.signupData,
    addressData: state.addressData,
    isPending: {
      register: registerMutation.isPending,
      saveAddress: saveAddressMutation.isPending || state.leaving,
      confirmRegister: confirmRegisterMutation.isPending || state.leaving,
      confirmLogin: confirmLoginMutation.isPending || state.leaving,
      resendRegister: resendRegisterMutation.isPending,
      resendLogin: resendLoginMutation.isPending,
    },
    actions: {
      submitSignup,
      submitAddress,
      confirmRegister: (values) => {
        dispatch({ type: "CLEAR_ERROR", field: "confirmRegister" });
        confirmRegisterMutation.mutate(values);
      },
      confirmLogin: (values) => {
        dispatch({ type: "CLEAR_ERROR", field: "confirmLogin" });
        confirmLoginMutation.mutate(values);
      },
      resendRegister: () => resendRegisterMutation.mutate(),
      resendLogin: () => resendLoginMutation.mutate(),
      clearEmailError: () => dispatch({ type: "CLEAR_ERROR", field: "signupEmail" }),
      goBack,
    },
  };
}
